import { Component, DestroyRef, inject, OnInit, ViewChild } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormGroup } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { ConfigurationWizardBackButtonComponent } from "@components/configuration-wizard-back-button/configuration-wizard-back-button.component";
import { ProductConfigStepperBarComponent } from "@components/stepper/product-config-stepper-bar/product-config-stepper-bar.component";
import { Family } from "@domain/product/monitoring/family/family";
import { FamilyStep } from "@domain/product/monitoring/family/family-step";
import { MonitoringCategory } from "@domain/product/monitoring/monitoring-product.service";
import { TelemetryConfiguration } from "@domain/project/configurations/monitoring/telemetry-configuration";
import { MonitoringProject as Project } from "@domain/project/monitoring-project";
import { TelemetrySelectorType } from "@domain/project/monitoring-selector-types.enum";
import { WizardComponent, WizardStepComponent } from "@odx/angular/components/wizard";
import { FloorplanMenuType } from "@project/floorplanner/floorplan-menu/fgds/floorplan-menu-type";
import { MonitoringProductItem } from "@project/monitoring-product-item";
import {
  buildTelemetryGroupedSourceItems,
  TelemetryGroupedPartLabels,
} from "@project/monitoring/grouped-parts/monitoring-grouped-parts-source-item.factory";
import {
  getMonitoringOtherProductsLabel,
  getTelemetryGroupedPartLabels,
} from "@project/monitoring/grouped-parts/monitoring-grouped-parts.helper";
import { MonitoringGroupedPartsService } from "@project/monitoring/grouped-parts/monitoring-grouped-parts.service";
import {
  MonitoringGroupedPartSection,
  MonitoringGroupedPartSourceItem,
} from "@project/monitoring/grouped-parts/monitoring-grouped-parts.types";
import { MonitoringOverviewComponent } from "@project/monitoring/grouped-parts/monitoring-overview.component";
import { MonitoringConfigurationFactory } from "@project/monitoring/monitoring-configuration.factory";
import {
  createFamilyVariantStep,
  getConfigurationOptionIdentifier,
  getSelectedFamilyOption,
} from "@project/monitoring/monitoring-configuration.utils";
import { FamilyVariantMode, isTelemetryFamilySteps } from "@project/monitoring/monitoring-family-wizard.utils";
import {
  OptionDisplayWithQuantity,
  OptionSelectorComponent,
} from "@project/monitoring/option-device-selector/option-selector.component";
import { OptionDisplay } from "@project/monitoring/option-display.service";
import { ProductFamilySelectorComponent } from "@project/monitoring/product-family-selector/product-family-selector.component";
import { of, Subject, switchMap } from "rxjs";

@Component({
  selector: "app-telemetry-configuration",
  imports: [
    ConfigurationWizardBackButtonComponent,
    WizardComponent,
    WizardStepComponent,
    ProductConfigStepperBarComponent,
    OptionSelectorComponent,
    MonitoringOverviewComponent,
    ProductFamilySelectorComponent,
  ],
  templateUrl: "./telemetry-configuration.component.html",
  styleUrl: "./telemetry-configuration.component.scss",
})
export class TelemetryConfigurationComponent implements OnInit {
  protected TelemetrySelectorTitleKeyMap: Record<TelemetrySelectorType, string> = {
    [TelemetrySelectorType.FAMILY]: $localize`:@@telemetryConfiguration.step1.title:Select family`,
    [TelemetrySelectorType.SENSOR]: $localize`:@@telemetryConfiguration.step2.title:Select sensor`,
    [TelemetrySelectorType.ADDITIONAL_BATTERIES]: $localize`:@@telemetryConfiguration.step3.title:Select additional batteries`,
    [TelemetrySelectorType.OVERVIEW]: $localize`:@@telemetryConfiguration.step4.title:Telemetry configuration summary`,
  };

  protected readonly overviewCategoryLabelMap: Partial<Record<TelemetrySelectorType, string>> = {
    [TelemetrySelectorType.FAMILY]: $localize`:@@telemetryConfiguration.overview.table.column1.category:Family`,
    [TelemetrySelectorType.SENSOR]: $localize`:@@telemetryConfiguration.overview.table.column2.category:Sensor`,
    [TelemetrySelectorType.ADDITIONAL_BATTERIES]: $localize`:@@telemetryConfiguration.overview.table.column3.category:Additional Batteries`,
  };
  protected readonly overviewCategoryLabels: TelemetryGroupedPartLabels = getTelemetryGroupedPartLabels();

  protected project!: Project;
  protected originalConfiguration?: TelemetryConfiguration;
  protected activeStep = 0;

  protected readonly FloorplanMenuType = FloorplanMenuType;
  protected readonly TelemetrySelectorType = TelemetrySelectorType;
  protected groupedSections: MonitoringGroupedPartSection[] = [];
  protected readonly otherProductsLabel = getMonitoringOtherProductsLabel();

  private activeRoute = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private monitoringGroupedPartsService = inject(MonitoringGroupedPartsService);
  private readonly overviewItems$ = new Subject<MonitoringGroupedPartSourceItem[]>();

  @ViewChild("configurationWizard")
  private configurationWizard!: WizardComponent;

  @ViewChild("backButton")
  private backButton!: ConfigurationWizardBackButtonComponent;

  protected selectedSensorForm!: FormGroup;
  protected selectedAdditionalBatteriesForm!: FormGroup;
  protected selectedFamilyForm!: FormGroup;
  protected selectedFamilyVariantForm!: FormGroup;

  protected family?: Family;
  protected familyVariantStep?: FamilyStep;
  protected sensorStep?: FamilyStep;
  protected additionalBatteriesStep?: FamilyStep;
  protected selectedFamily?: OptionDisplay;
  protected selectedFamilyVariant?: OptionDisplayWithQuantity[];
  protected selectedSensor?: OptionDisplayWithQuantity;
  protected selectedAdditionalBatteries?: OptionDisplayWithQuantity[];
  protected originalFamilyVariantOptions: MonitoringProductItem[] = [];
  protected originalSensorOptions: MonitoringProductItem[] = [];
  protected originalAdditionalBatteries: MonitoringProductItem[] = [];
  private restoredFamilyVariantOptions: MonitoringProductItem[] = [];
  private restoredSensorOptions: MonitoringProductItem[] = [];
  private restoredAdditionalBatteries: MonitoringProductItem[] = [];

  constructor() {
    this.overviewItems$
      .pipe(
        switchMap((items) =>
          items.length
            ? this.monitoringGroupedPartsService.buildSections(items, MonitoringCategory.TELEMETRY, this.otherProductsLabel)
            : of([]),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((sections) => {
        this.groupedSections = sections;
      });
  }

  ngOnInit(): void {
    this.project = this.activeRoute.parent!.snapshot.data["project"];

    if (this.activeRoute.snapshot.queryParams["config_id"]) {
      const configId = this.activeRoute.snapshot.queryParams["config_id"];
      this.originalConfiguration = this.project.telemetries.find((telemetry) => telemetry.id === configId);
      this.restoredFamilyVariantOptions = [...(this.originalConfiguration?.family ?? [])];
      this.restoredSensorOptions = this.originalConfiguration?.sensor ? [this.originalConfiguration.sensor] : [];
      this.restoredAdditionalBatteries = this.originalConfiguration?.additionalBatteries ?? [];
      this.originalFamilyVariantOptions = [...this.restoredFamilyVariantOptions];
      this.originalSensorOptions = [...this.restoredSensorOptions];
      this.originalAdditionalBatteries = [...this.restoredAdditionalBatteries];
    }
  }

  protected setSelectedFamily($event: OptionDisplay | undefined) {
    this.selectedFamily = $event;
    this.family = $event?.family;
    const familyVariant = $event?.familyVariant?.variant;
    this.familyVariantStep =
      familyVariant?.mode === FamilyVariantMode.MULTIPLE_OPTIONS ? createFamilyVariantStep(familyVariant.step) : undefined;
    const normalizedSteps = isTelemetryFamilySteps($event?.familyVariant?.steps) ? $event.familyVariant.steps : undefined;
    this.sensorStep = normalizedSteps?.sensorStep ?? this.family?.steps?.[1];
    this.additionalBatteriesStep = normalizedSteps?.additionalBatteriesStep ?? this.family?.steps?.[2];

    this.selectedFamilyVariant =
      familyVariant?.mode === FamilyVariantMode.SINGLE_OPTION && $event
        ? [{ optionDisplay: { ...$event, option: familyVariant.option }, quantity: 1 }]
        : undefined;
    this.selectedSensor = undefined;
    this.selectedAdditionalBatteries = undefined;

    if (this.originalConfiguration && $event?.family?.familyNumber === this.originalConfiguration.familyNumber) {
      this.originalFamilyVariantOptions = [...this.restoredFamilyVariantOptions];
      this.originalSensorOptions = [...this.restoredSensorOptions];
      this.originalAdditionalBatteries = [...this.restoredAdditionalBatteries];
    } else {
      this.originalFamilyVariantOptions = [];
      this.originalSensorOptions = [];
      this.originalAdditionalBatteries = [];
    }
    this.overviewItems$.next([]);
  }

  protected setSelectedFamilyVariant($event: OptionDisplayWithQuantity[]) {
    this.selectedFamilyVariant = $event;
    this.overviewItems$.next([]);
  }

  protected setSelectedSensor($event: OptionDisplayWithQuantity[]) {
    this.selectedSensor = $event[0];
    if (
      this.isOriginalFamilySelected() &&
      this.isSameAsOriginalOption(this.selectedSensor?.optionDisplay, this.originalConfiguration?.sensor)
    ) {
      this.originalAdditionalBatteries = [...this.restoredAdditionalBatteries];
    } else {
      this.originalAdditionalBatteries = [];
    }
    this.overviewItems$.next([]);
  }

  protected setSelectedAdditionalBatteries($event: OptionDisplayWithQuantity[]) {
    this.selectedAdditionalBatteries = $event;
    this.overviewItems$.next([]);
  }

  protected onSubmit() {
    if (this.activeStep === 0) {
      this.selectedFamilyForm.markAllAsTouched();
      if (!this.selectedFamilyForm.valid) return;
    }

    if (this.activeStep === this.familyVariantStepIndex) {
      this.selectedFamilyVariantForm.markAllAsTouched();
      if (!this.selectedFamilyVariantForm.valid) return;
    }

    if (this.activeStep === this.sensorStepIndex) {
      this.selectedSensorForm.markAllAsTouched();
      if (!this.selectedSensorForm.valid) return;
    }

    if (this.activeStep === this.additionalBatteriesStepIndex) {
      this.selectedAdditionalBatteriesForm.markAllAsTouched();
      if (!this.selectedAdditionalBatteriesForm.valid) return;
    }

    if (this.activeStep === this.lastSelectionStepIndex) this.collectOverviewData();

    if (this.activeStep === this.summaryStepIndex) {
      if (this.originalConfiguration) {
        this.updateTelemetryConfig(this.originalConfiguration);
      } else {
        this.createNewTelemetryConfig();
      }
    }

    this.configurationWizard.setActiveStepValid();
    this.configurationWizard.nextStep();
  }

  protected onPrevious() {
    this.configurationWizard.previousStep();
  }

  protected readonly MonitoringCategory = MonitoringCategory;

  protected get familyVariantStepIndex(): number {
    return this.hasFamilyVariantStep ? 1 : -1;
  }

  protected get sensorStepIndex(): number {
    return this.hasSensorStep ? 1 + (this.hasFamilyVariantStep ? 1 : 0) : -1;
  }

  protected get additionalBatteriesStepIndex(): number {
    return this.hasAdditionalBatteriesStep ? 1 + (this.hasFamilyVariantStep ? 1 : 0) + (this.hasSensorStep ? 1 : 0) : -1;
  }

  protected get summaryStepIndex(): number {
    return 1 + (this.hasFamilyVariantStep ? 1 : 0) + (this.hasSensorStep ? 1 : 0) + (this.hasAdditionalBatteriesStep ? 1 : 0);
  }

  protected get lastSelectionStepIndex(): number {
    return this.additionalBatteriesStepIndex !== -1
      ? this.additionalBatteriesStepIndex
      : this.sensorStepIndex !== -1
        ? this.sensorStepIndex
        : this.familyVariantStepIndex !== -1
          ? this.familyVariantStepIndex
          : 0;
  }

  protected get steps(): number {
    return this.summaryStepIndex + 1;
  }

  protected get hasFamilyVariantStep(): boolean {
    return !!this.familyVariantStep?.options?.length;
  }

  protected get hasSensorStep(): boolean {
    return this.family ? !!this.sensorStep?.options?.length : true;
  }

  protected get hasAdditionalBatteriesStep(): boolean {
    return this.family ? !!this.additionalBatteriesStep?.options?.length : true;
  }

  private updateTelemetryConfig(config: TelemetryConfiguration) {
    const selectedFamilyOption = getSelectedFamilyOption(this.selectedFamily, this.selectedFamilyVariant?.[0]?.optionDisplay);
    const family = this.selectedFamilyVariant?.length
      ? this.selectedFamilyVariant.map(({ optionDisplay, quantity }) =>
          MonitoringProductItem.create(optionDisplay.option, quantity),
        )
      : selectedFamilyOption
        ? [MonitoringProductItem.create(selectedFamilyOption.option, 1)]
        : undefined;
    config.update(
      selectedFamilyOption?.displayName || this.family!.familyName,
      this.family!.familyNumber,
      family,
      this.selectedSensor
        ? MonitoringProductItem.create(this.selectedSensor.optionDisplay.option, this.selectedSensor.quantity)
        : undefined,
      this.selectedAdditionalBatteries?.map((additionalBattery) =>
        MonitoringProductItem.create(additionalBattery.optionDisplay.option, additionalBattery.quantity),
      ),
    );
    this.backButton.navigateBack();
  }

  private createNewTelemetryConfig() {
    const selectedFamilyOption = getSelectedFamilyOption(this.selectedFamily, this.selectedFamilyVariant?.[0]?.optionDisplay);
    const family = this.selectedFamilyVariant?.length
      ? this.selectedFamilyVariant.map(({ optionDisplay, quantity }) =>
          MonitoringProductItem.create(optionDisplay.option, quantity),
        )
      : selectedFamilyOption
        ? [MonitoringProductItem.create(selectedFamilyOption.option, 1)]
        : undefined;
    const telemetryConfig = MonitoringConfigurationFactory.createTelemetry(
      selectedFamilyOption?.displayName || this.family!.familyName,
      this.family!,
      this.project,
      {
        family,
        sensor: this.selectedSensor
          ? MonitoringProductItem.create(this.selectedSensor.optionDisplay.option, this.selectedSensor.quantity)
          : undefined,
        additionalBatteries: this.selectedAdditionalBatteries?.map((additionalBattery) =>
          MonitoringProductItem.create(additionalBattery.optionDisplay.option, additionalBattery.quantity),
        ),
      },
    );

    this.project.addTelemetries(telemetryConfig);
    this.backButton.navigateBack();
  }

  private collectOverviewData() {
    const selectedFamilyOption = getSelectedFamilyOption(this.selectedFamily, this.selectedFamilyVariant?.[0]?.optionDisplay);
    const familyItems = this.selectedFamilyVariant?.length
      ? this.selectedFamilyVariant.map(({ optionDisplay, quantity }) => ({ option: optionDisplay.option, quantity }))
      : selectedFamilyOption
        ? { option: selectedFamilyOption.option, quantity: 1 }
        : undefined;
    const items = buildTelemetryGroupedSourceItems(
      {
        family: familyItems,
        sensor: this.selectedSensor
          ? { option: this.selectedSensor.optionDisplay.option, quantity: this.selectedSensor.quantity }
          : undefined,
        additionalBatteries: this.selectedAdditionalBatteries?.map((battery) => ({
          option: battery.optionDisplay.option,
          quantity: battery.quantity,
        })),
      },
      this.overviewCategoryLabels,
    );
    this.overviewItems$.next(items);
  }

  private isOriginalFamilySelected(): boolean {
    return !!this.originalConfiguration && this.family?.familyNumber === this.originalConfiguration.familyNumber;
  }

  private isSameAsOriginalOption(option: OptionDisplay | undefined, original: MonitoringProductItem | undefined): boolean {
    if (!option || !original || !this.originalConfiguration) return false;

    return (
      getConfigurationOptionIdentifier(this.family?.familyName, option.option) ===
      getConfigurationOptionIdentifier(this.family?.familyName, original.option)
    );
  }
}
